import { useQuery } from "@tanstack/react-query";

import { useAuth } from "~/hooks/useAuth";
import type { Tables, TablesInsert, TablesUpdate } from "~/api/tables";
import { unwrap } from "@brad-tipper/app-frontend";

import { api } from "~/api/client";
import { db } from "~/api/db";
import {
  parseTagNames,
  requireText,
  resolveTimeEntryLinks,
  resolveTimeEntryRateCents,
  rootKey,
  workspaceKey,
  taskPriority,
  textOrNull,
  useWorkspaceMutation,
  type RecurringTaskRule,
  type Task,
  type TimeEntry,
  type TimeEntryTag,
} from "./shared";

export function useWorkspaceTasks(workspaceId: string | undefined) {
  const query = useQuery({
    queryKey: workspaceKey("tasks", workspaceId),
    queryFn: async () => {
      if (!workspaceId) return [] as Task[];
      const { data, error } = await db
        .from("tasks")
        .select("*")
        .eq("workspace_id", workspaceId)
        .order("created_at", { ascending: false });
      if (error) throw error;
      return (data ?? []) as Task[];
    },
    enabled: Boolean(workspaceId),
  });

  const create = useWorkspaceMutation(
    workspaceId,
    "tasks",
    async (input: Record<string, string>, targetWorkspaceId) => {
      const { error } = await db.from("tasks").insert({
        workspace_id: targetWorkspaceId,
        project_id: textOrNull(input.projectId),
        name: requireText(input.name, "Task name"),
        description: textOrNull(input.description),
        due_on: textOrNull(input.dueOn),
        priority: taskPriority(input.priority),
        status: "todo",
        billable: input.billable !== "false",
      } satisfies TablesInsert<"tasks">);
      if (error) throw error;
    },
  );

  const updateStatus = useWorkspaceMutation(
    workspaceId,
    "tasks",
    async (input: {
      id: string;
      status: "todo" | "doing" | "done" | "archived";
    }) => {
      const { error } = await db
        .from("tasks")
        .update({ status: input.status })
        .eq("id", input.id);
      if (error) throw error;
    },
  );

  const setBillable = useWorkspaceMutation(
    workspaceId,
    "tasks",
    async (input: { id: string; billable: boolean }) => {
      const { error } = await db
        .from("tasks")
        .update({ billable: input.billable })
        .eq("id", input.id);
      if (error) throw error;
    },
  );

  const updatePriority = useWorkspaceMutation(
    workspaceId,
    "tasks",
    async (input: { id: string; priority: Task["priority"] }) => {
      const { error } = await db
        .from("tasks")
        .update({ priority: input.priority })
        .eq("id", input.id);
      if (error) throw error;
    },
  );

  const update = useWorkspaceMutation(
    workspaceId,
    "tasks",
    async (input: { id: string; values: Record<string, string> }) => {
      const { error } = await db
        .from("tasks")
        .update({
          description: textOrNull(input.values.description),
          due_on: textOrNull(input.values.dueOn),
          name: requireText(input.values.name, "Task name"),
          priority: taskPriority(input.values.priority),
          project_id: textOrNull(input.values.projectId),
          billable: input.values.billable !== "false",
        } satisfies TablesUpdate<"tasks">)
        .eq("id", input.id);
      if (error) throw error;
    },
  );

  return {
    ...query,
    create,
    setBillable,
    update,
    updatePriority,
    updateStatus,
  };
}

export function useWorkspaceRecurringTasks(workspaceId: string | undefined) {
  const query = useQuery({
    queryKey: workspaceKey("recurring-tasks", workspaceId),
    queryFn: async () => {
      if (!workspaceId) return [] as RecurringTaskRule[];
      const { data, error } = await db
        .from("recurring_task_rules")
        .select("*")
        .eq("workspace_id", workspaceId)
        .order("next_due_on", { ascending: true });
      if (error) throw error;
      return (data ?? []) as RecurringTaskRule[];
    },
    enabled: Boolean(workspaceId),
  });

  function payload(input: Record<string, string>, targetWorkspaceId: string) {
    const clientEmailEnabled = input.clientEmailEnabled === "true";
    const subject = textOrNull(input.clientEmailSubject);
    if (clientEmailEnabled && !input.clientId) {
      throw new Error("Choose a client before enabling client email.");
    }
    if (clientEmailEnabled && !subject) {
      throw new Error("Add a client email subject.");
    }
    const leadDays = Number(input.createDaysBefore || 0);
    if (!Number.isInteger(leadDays) || leadDays < 0 || leadDays > 90) {
      throw new Error("Create-ahead days must be between 0 and 90.");
    }
    return {
      workspace_id: targetWorkspaceId,
      client_id: textOrNull(input.clientId),
      project_id: textOrNull(input.projectId),
      name: requireText(input.name, "Task name"),
      description: textOrNull(input.description),
      cadence:
        input.cadence === "weekly" ||
        input.cadence === "fortnightly" ||
        input.cadence === "every_2_months" ||
        input.cadence === "quarterly" ||
        input.cadence === "yearly"
          ? input.cadence
          : "monthly",
      next_due_on: requireText(input.nextDueOn, "Next due date"),
      create_days_before: leadDays,
      priority: taskPriority(input.priority),
      billable: input.billable !== "false",
      dashboard_notify: input.dashboardNotify !== "false",
      client_email_enabled: clientEmailEnabled,
      client_email_subject: subject,
      client_email_body: textOrNull(input.clientEmailBody),
      enabled: input.enabled !== "false",
    } satisfies TablesInsert<"recurring_task_rules">;
  }

  const create = useWorkspaceMutation(
    workspaceId,
    "recurring-tasks",
    async (input: Record<string, string>, targetWorkspaceId) => {
      const { error } = await db
        .from("recurring_task_rules")
        .insert(payload(input, targetWorkspaceId));
      if (error) throw error;
    },
  );

  const update = useWorkspaceMutation(
    workspaceId,
    "recurring-tasks",
    async (
      input: { id: string; values: Record<string, string> },
      targetWorkspaceId,
    ) => {
      const values = payload(input.values, targetWorkspaceId);
      const { workspace_id: workspaceIdToOmit, ...changes } = values;
      void workspaceIdToOmit;
      const { error } = await db
        .from("recurring_task_rules")
        .update(changes satisfies TablesUpdate<"recurring_task_rules">)
        .eq("workspace_id", targetWorkspaceId)
        .eq("id", input.id);
      if (error) throw error;
    },
  );

  const remove = useWorkspaceMutation(
    workspaceId,
    "recurring-tasks",
    async (id: string, targetWorkspaceId) => {
      const { error } = await db
        .from("recurring_task_rules")
        .delete()
        .eq("workspace_id", targetWorkspaceId)
        .eq("id", id);
      if (error) throw error;
    },
  );

  const runDue = useWorkspaceMutation(
    workspaceId,
    "recurring-tasks",
    async (_input: undefined, targetWorkspaceId) => {
      return unwrap(
        api.runRecurringTasks({ body: { workspace_id: targetWorkspaceId } }),
      );
    },
  );

  return { ...query, create, remove, rules: query.data ?? [], runDue, update };
}

export function useWorkspaceTimeEntryTags(workspaceId: string | undefined) {
  return useQuery({
    queryKey: workspaceKey("time-entry-tags", workspaceId),
    queryFn: async () => {
      if (!workspaceId) return [] as TimeEntryTag[];
      const { data, error } = await db
        .from("time_entry_tags")
        .select("*")
        .eq("workspace_id", workspaceId)
        .order("name", { ascending: true });
      if (error) throw error;
      return (data ?? []) as TimeEntryTag[];
    },
    enabled: Boolean(workspaceId),
  });
}

async function applyTimeEntryTags(
  timeEntryId: string,
  tags: string | undefined,
) {
  await unwrap(
    api.setTimeEntryTags({
      path: { id: timeEntryId },
      body: { names: parseTagNames(tags) },
    }),
  );
}

function mapTimeEntries(
  rows: Array<
    Tables<"time_entries"> & {
      time_entry_tag_links?: Array<{
        time_entry_tags: { id: string; name: string } | null;
      }>;
    }
  >,
): TimeEntry[] {
  return rows.map((row) => {
    const { time_entry_tag_links: links, ...entry } = row;
    return {
      ...entry,
      tags: (links ?? [])
        .map((link) => link.time_entry_tags)
        .filter((tag): tag is { id: string; name: string } => Boolean(tag)),
    };
  });
}

export function useWorkspaceTimeEntries(workspaceId: string | undefined) {
  const { user } = useAuth();
  const query = useQuery({
    queryKey: workspaceKey("time-entries", workspaceId),
    queryFn: async () => {
      if (!workspaceId) return [] as TimeEntry[];
      const { data, error } = await db
        .from("time_entries")
        .select("*, time_entry_tag_links(time_entry_tags(id, name))")
        .eq("workspace_id", workspaceId)
        .order("started_at", { ascending: false });
      if (error) throw error;
      return mapTimeEntries(
        (data ?? []) as Parameters<typeof mapTimeEntries>[0],
      );
    },
    enabled: Boolean(workspaceId),
  });

  const create = useWorkspaceMutation(
    workspaceId,
    "time-entries",
    async (input: Record<string, string>, targetWorkspaceId) => {
      if (!user) throw new Error("Sign in to create time entries.");
      const description = requireText(input.description, "Description");
      const minutes = Math.max(1, Math.round(Number(input.minutes) || 60));
      const hourlyRate = Number(input.hourlyRate);
      const startedAt = input.startedAt
        ? new Date(input.startedAt)
        : new Date();
      if (Number.isNaN(startedAt.getTime())) {
        throw new Error("Start time is invalid.");
      }
      const endedAt = new Date(startedAt.getTime() + minutes * 60 * 1000);
      const links = await resolveTimeEntryLinks(targetWorkspaceId, input);
      const billable = input.billable !== "false";
      const hourlyRateCents = billable
        ? Number.isFinite(hourlyRate) && hourlyRate > 0
          ? Math.round(hourlyRate * 100)
          : await resolveTimeEntryRateCents({
              clientId: links.clientId,
              date: startedAt.toISOString().slice(0, 10),
              projectId: links.projectId,
              userId: user.id,
              workspaceId: targetWorkspaceId,
            })
        : null;
      const { data, error } = await db
        .from("time_entries")
        .insert({
          workspace_id: targetWorkspaceId,
          user_id: user.id,
          client_id: links.clientId,
          project_id: links.projectId,
          task_id: links.taskId,
          source: "manual",
          description,
          started_at: startedAt.toISOString(),
          ended_at: endedAt.toISOString(),
          duration_seconds: minutes * 60,
          billable,
          hourly_rate_cents: hourlyRateCents,
        } satisfies TablesInsert<"time_entries">)
        .select("id")
        .single();
      if (error) throw error;
      await applyTimeEntryTags(data.id, input.tags);
    },
  );

  const createFromRange = useWorkspaceMutation(
    workspaceId,
    "time-entries",
    async (
      input: {
        description: string;
        clientId?: string;
        projectId?: string;
        taskId?: string;
        billable?: boolean | string;
        hourlyRate?: string | number;
        startedAt: string;
        endedAt: string;
        tags?: string;
      },
      targetWorkspaceId,
    ) => {
      if (!user) throw new Error("Sign in to create time entries.");
      const description = requireText(input.description, "Description");
      const startedAt = new Date(input.startedAt);
      const endedAt = new Date(input.endedAt);
      if (
        Number.isNaN(startedAt.getTime()) ||
        Number.isNaN(endedAt.getTime())
      ) {
        throw new Error("Start or end time is invalid.");
      }
      if (endedAt <= startedAt) {
        throw new Error("End time must be after start time.");
      }
      const durationSeconds = Math.max(
        60,
        Math.round((endedAt.getTime() - startedAt.getTime()) / 1000),
      );
      const hourlyRate = Number(input.hourlyRate);
      const links = await resolveTimeEntryLinks(targetWorkspaceId, input);
      const billable =
        input.billable === false || input.billable === "false" ? false : true;
      const hourlyRateCents = billable
        ? Number.isFinite(hourlyRate) && hourlyRate > 0
          ? Math.round(hourlyRate * 100)
          : await resolveTimeEntryRateCents({
              clientId: links.clientId,
              date: startedAt.toISOString().slice(0, 10),
              projectId: links.projectId,
              userId: user.id,
              workspaceId: targetWorkspaceId,
            })
        : null;
      const { data, error } = await db
        .from("time_entries")
        .insert({
          workspace_id: targetWorkspaceId,
          user_id: user.id,
          client_id: links.clientId,
          project_id: links.projectId,
          task_id: links.taskId,
          source: "manual",
          description,
          started_at: startedAt.toISOString(),
          ended_at: endedAt.toISOString(),
          duration_seconds: durationSeconds,
          billable,
          hourly_rate_cents: hourlyRateCents,
        } satisfies TablesInsert<"time_entries">)
        .select("id")
        .single();
      if (error) throw error;
      await applyTimeEntryTags(data.id, input.tags);
    },
  );

  const startTimerEntry = useWorkspaceMutation(
    workspaceId,
    "time-entries",
    async (
      input: {
        clientId?: string;
        description: string;
        billable?: string;
        hourlyRate?: string;
        projectId?: string;
        taskId?: string;
        tags?: string;
      },
      targetWorkspaceId,
    ) => {
      if (!user) throw new Error("Sign in to start a timer.");
      const { data: runningTimer, error: runningTimerError } = await db
        .from("time_entries")
        .select("id, workspace_id")
        .eq("user_id", user.id)
        .is("ended_at", null)
        .maybeSingle();
      if (runningTimerError) throw runningTimerError;
      if (runningTimer) {
        throw new Error(
          runningTimer.workspace_id === targetWorkspaceId
            ? "You already have a timer running."
            : "Stop your running timer in the other workspace first.",
        );
      }

      const startedAt = new Date();
      const hourlyRate = Number(input.hourlyRate);
      const billable = input.billable !== "false";
      const links = await resolveTimeEntryLinks(targetWorkspaceId, input);
      const hourlyRateCents = billable
        ? Number.isFinite(hourlyRate) && hourlyRate > 0
          ? Math.round(hourlyRate * 100)
          : await resolveTimeEntryRateCents({
              clientId: links.clientId,
              date: startedAt.toISOString().slice(0, 10),
              projectId: links.projectId,
              userId: user.id,
              workspaceId: targetWorkspaceId,
            })
        : null;
      const { data, error } = await db
        .from("time_entries")
        .insert({
          workspace_id: targetWorkspaceId,
          user_id: user.id,
          client_id: links.clientId,
          project_id: links.projectId,
          task_id: links.taskId,
          source: "timer",
          description: requireText(input.description, "Description"),
          started_at: startedAt.toISOString(),
          ended_at: null,
          duration_seconds: 0,
          billable,
          hourly_rate_cents: hourlyRateCents,
        } satisfies TablesInsert<"time_entries">)
        .select("id")
        .single();
      if (error) throw error;
      await applyTimeEntryTags(data.id, input.tags);
    },
  );

  const stopTimerEntry = useWorkspaceMutation(
    workspaceId,
    "time-entries",
    async (input: { id: string; endedAt: Date }, targetWorkspaceId) => {
      if (!user) throw new Error("Sign in to stop a timer.");
      const { data: entry, error: loadError } = await db
        .from("time_entries")
        .select("started_at")
        .eq("id", input.id)
        .eq("workspace_id", targetWorkspaceId)
        .eq("user_id", user.id)
        .is("ended_at", null)
        .single();
      if (loadError) throw loadError;

      const durationSeconds = Math.max(
        1,
        Math.round(
          (input.endedAt.getTime() - new Date(entry.started_at).getTime()) /
            1000,
        ),
      );
      const { error } = await db
        .from("time_entries")
        .update({
          duration_seconds: durationSeconds,
          ended_at: input.endedAt.toISOString(),
        } satisfies TablesUpdate<"time_entries">)
        .eq("id", input.id)
        .eq("workspace_id", targetWorkspaceId)
        .eq("user_id", user.id)
        .is("ended_at", null);
      if (error) throw error;
    },
  );

  const discardTimerEntry = useWorkspaceMutation(
    workspaceId,
    "time-entries",
    async (id: string, targetWorkspaceId) => {
      if (!user) throw new Error("Sign in to discard a timer.");
      const { error } = await db
        .from("time_entries")
        .delete()
        .eq("id", id)
        .eq("workspace_id", targetWorkspaceId)
        .eq("user_id", user.id)
        .is("ended_at", null);
      if (error) throw error;
    },
  );

  const update = useWorkspaceMutation(
    workspaceId,
    "time-entries",
    async (
      input: { id: string; values: Record<string, string> },
      targetWorkspaceId,
    ) => {
      if (!user) throw new Error("Sign in to update time entries.");
      const minutes = Math.max(
        1,
        Math.round(Number(input.values.minutes) || 60),
      );
      const hourlyRate = Number(input.values.hourlyRate);
      const { data: existing, error: loadError } = await db
        .from("time_entries")
        .select("started_at, ended_at, invoiced_at")
        .eq("id", input.id)
        .eq("workspace_id", targetWorkspaceId)
        .single();

      if (loadError) throw loadError;
      if (existing.invoiced_at) {
        throw new Error("Invoiced time entries cannot be edited.");
      }
      if (!existing.ended_at) {
        throw new Error("Stop the running timer before editing this entry.");
      }

      const startedAt = new Date(existing.started_at);
      const endedAt = new Date(startedAt.getTime() + minutes * 60 * 1000);
      const links = await resolveTimeEntryLinks(
        targetWorkspaceId,
        input.values,
      );
      const billable = input.values.billable !== "false";
      const hourlyRateCents = billable
        ? Number.isFinite(hourlyRate) && hourlyRate > 0
          ? Math.round(hourlyRate * 100)
          : await resolveTimeEntryRateCents({
              clientId: links.clientId,
              date: startedAt.toISOString().slice(0, 10),
              projectId: links.projectId,
              userId: user.id,
              workspaceId: targetWorkspaceId,
            })
        : null;
      const { error } = await db
        .from("time_entries")
        .update({
          billable,
          client_id: links.clientId,
          description: requireText(input.values.description, "Description"),
          duration_seconds: minutes * 60,
          ended_at: endedAt.toISOString(),
          hourly_rate_cents: hourlyRateCents,
          project_id: links.projectId,
          task_id: links.taskId,
        } satisfies TablesUpdate<"time_entries">)
        .eq("id", input.id)
        .eq("workspace_id", targetWorkspaceId)
        .is("invoiced_at", null);
      if (error) throw error;
      await applyTimeEntryTags(input.id, input.values.tags);
    },
  );

  return {
    ...query,
    create,
    createFromRange,
    discardTimerEntry,
    startTimerEntry,
    stopTimerEntry,
    update,
  };
}
